/**
 * Governance Layout
 *
 * Story FOS-4.2.6: Master Components & Pages
 * Task 5: Create GovernanceLayout page
 *
 * Layout component for governance section with navigation between
 * discussions, proposals, and voting pages.
 *
 * @module pages/Governance
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { MessageSquare, FileText, Vote, ChevronRight, LogOut } from 'lucide-react';
import { SuiteSwitcher } from '@hello-world-dao/ui';
import { useAuth } from '@hello-world-dao/auth';
import { SwitchToFosButton } from '@/components/layout/SwitchToFosButton';
import { resolveFosFrontdoorLoginUrl } from '@/components/layout/fos-frontdoor-url';
import { logoutIdentitySession } from '@/services/identity-service-client';
import { clearIdentityJwt } from '@/services/identity-jwt-store';

/**
 * Navigation items for governance section
 */
const NAV_ITEMS = [
  {
    path: '/discussions',
    label: 'Discussions',
    icon: MessageSquare,
    description: 'Collaborative proposal refinement',
  },
  {
    path: '/proposals',
    label: 'Proposals',
    icon: FileText,
    description: 'Active and past proposals',
  },
  {
    path: '/voting',
    label: 'Voting',
    icon: Vote,
    description: 'Cast your votes',
  },
];

/**
 * Get breadcrumb text from path
 */
function getBreadcrumb(pathname: string): string | null {
  if (pathname.includes('/discussions/')) {
    return 'Discussion';
  }
  if (pathname.includes('/proposals/')) {
    return 'Proposal';
  }
  return null;
}

/**
 * Governance section layout with navigation
 */
export function GovernanceLayout() {
  const location = useLocation();
  const breadcrumb = getBreadcrumb(location.pathname);
  const { logout } = useAuth();

  async function handleLogout() {
    await logout();
    // cross-suite-session-2026-06-18: also tear down the identity-service
    // session — revoke the refresh-token family + clear the httpOnly
    // hwdao_refresh cookie, then drop the in-memory access JWT — so a logout
    // here does not leave a cookie that silently rehydrates the member on the
    // next load (or on another suite).
    await logoutIdentitySession();
    clearIdentityJwt();
    // tt-rm-3: post-logout, re-authenticate at the unified UID front-door
    // (returning to the FOS dashboard). The old think-tank login host is
    // deprecated (unified under FounderyOS) and dead.
    window.location.href = resolveFosFrontdoorLoginUrl();
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Title and breadcrumb */}
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-foreground">
                Governance
              </h1>
              {breadcrumb && (
                <>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {breadcrumb}
                  </span>
                </>
              )}
            </div>

            {/* SuiteSwitcher + Logout */}
            <div className="flex items-center gap-3">
              {/* PLATFORM-003.2 — cross-domain nav to FounderyOS */}
              <SwitchToFosButton />
              <SuiteSwitcher
                currentSuite="governance"
                suiteUrls={{
                  // tt-rm-3: Think Tank is deprecated (unified under FounderyOS).
                  // Omitting the URL hides it from the switcher menu.
                  portal: import.meta.env.VITE_DAO_FRONTEND_URL,
                  otterCamp: import.meta.env.VITE_OTTER_CAMP_URL,
                  admin: import.meta.env.VITE_ADMIN_SUITE_URL,
                }}
              />
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>

          {/* Navigation tabs */}
          <nav className="flex gap-8 -mb-px">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}

export default GovernanceLayout;
